/**
 * Idle-gated falling autumn leaves — a config of the shared falling-particle
 * effect. Maple-leaf sprites tumble down in a mix of autumn colors (each leaf
 * picks one of the `--leaf-1..4` theme vars), so the flock recolors light/dusk.
 */
import { createFallingEffect } from './createFallingEffect';

const LEAF_VARS = ['--leaf-1', '--leaf-2', '--leaf-3', '--leaf-4'];
const DEFAULT_LEAF = 'oklch(0.65 0.16 55)';
const FALLBACKS = [
  'oklch(0.65 0.16 55)',
  'oklch(0.55 0.18 30)',
  'oklch(0.74 0.14 82)',
  'oklch(0.50 0.12 48)',
];

// Stylized maple leaf, drawn in a ~20-unit box. Built lazily (no Path2D in jsdom).
const LEAF_PATH_D =
  'M0,-10 L2.5,-4 L7,-6 L4,-1 L9,1 L4,2.5 L6,8 L1,4.5 L0,10 L-1,4.5 L-6,8 L-4,2.5 L-9,1 L-4,-1 L-7,-6 L-2.5,-4 Z';
let leafPath: Path2D | undefined;

/** Draw a maple leaf centered at the origin (engine sets fill + transform). */
function drawLeaf(ctx: CanvasRenderingContext2D, size: number): void {
  leafPath ??= new Path2D(LEAF_PATH_D);
  ctx.save();
  ctx.scale(size / 10, size / 10);
  ctx.fill(leafPath);
  ctx.restore();
}

/** Full-screen idle falling autumn leaves (dense, multi-color, tumbling). */
export const LeavesOverlay = createFallingEffect({
  testId: 'leaves-overlay',
  config: {
    count: 50,
    size: [9, 16],
    speed: [0.6, 1.4],
    drift: [-0.4, 0.4],
    sway: [16, 34],
    swayFreq: [0.012, 0.03],
    spin: [-0.06, 0.06],
    alpha: [0.85, 1],
  },
  fill: canvas =>
    LEAF_VARS.map(
      (v, i) => getComputedStyle(canvas).getPropertyValue(v).trim() || FALLBACKS[i] || DEFAULT_LEAF
    ),
  draw: drawLeaf,
});
